<template>
    <div class="input-file" :class="{'is-invalid': error}">
        <div
            class="input-file__inner"
            :class="{
                'input-file--drag-over': dragOver
            }"

            @dragover.stop.prevent="handleDragOver"
            @dragleave.stop.prevent="handleDragEnd"
            @drop.stop.prevent="handleFileSelect"
        >
            <div
                v-if="files.length < max"
                class="input-file__placeholder"
                @click.stop.prevent="handleClick"
            >
                <slot>Drop files here ...</slot>
            </div>
            <div v-if="list" class="input-file__list" @click.stop.prevent="handleClick">
                <div
                    v-for="file in files"
                    class="input-file__list-item"
                    :key="file.name"
                    :class="getExtCssClass(file.ext)"
                >
                    <slot name="file" :file="file">
                        <div
                            class="input-file__list-type"
                            :style="getAsBackgroundImage(file.image)"
                        >
                            {{ file.ext }}
                        </div>
                        <div class="input-file__list-name">
                            {{ file.name }}
                        </div>
                        <div class="input-file__list-size">
                            {{ file.size }}
                        </div>
                    </slot>
                </div>
            </div>
            <input
                type="file"
                class="input-file__input"
                ref="input"
                aria-label="Datei auswählen"
                :accept="accept"
                :multiple="max > 1"
                @change="handleFileSelect"
            >
        </div>
    </div>
</template>

<script>
export default {
    name: 'InputFile',

    props: {
        accept: {
            type: String,
            default: ''
        },
        error: {
            type: Boolean,
            default: false
        },
        max: {
            type: Number,
            default: 1
        },
        min: {
            type: Number,
            default: 0
        },
        dragAndDrop: {
            type: Boolean,
            default: true
        },
        list: {
            type: Boolean,
            default: true
        },
        storedFiles: {
            type: Array,
            default: function () {
                return []
            }
        },
        requirements: {
            type: Object,
            default () {
                return {
                    maxSize: Infinity,
                    minSize: 0,
                    maxWidth: Infinity,
                    minWidth: 0,
                    maxHeight: Infinity,
                    minHeight: 0
                }
            }
        }
    },

    data () {
        return {
            support: {
                file: true,
                fileReader: true,
                fileList: true,
                blob: true
            },
            files: [],
            fileList: null,
            dragOver: false
        }
    },

    mounted () {
        this.support = {
            file: window.File,
            fileReader: window.FileReader,
            fileList: window.FileList,
            blob: window.Blob
        }

        if (!this.storedFiles.length) {
            return
        }

        this.files = this.storedFiles
    },

    methods: {
        getExtCssClass (ext) {
            return 'input-file--' + ext
        },

        getAsBackgroundImage (image) {
            return image ? 'background-image: url(' + image.src + ');' : ''
        },

        handleFileSelect (event) {
            // FileList
            this.fileList = event.target.files || event.dataTransfer.files
            this.files = this.convertFileList(this.fileList)
            // Make sure dragOver is reset
            this.handleDragEnd()
            // Propagate new files
            this.$emit('input', this.files)
        },

        handleClick (event) {
            this.$refs.input.click()
        },

        handleDragOver (event) {
            event.dataTransfer.dropEffect = 'copy'
            this.dragOver = true
        },

        handleDragEnd (event) {
            this.dragOver = false
        },

        fileSize (size) {
            if (size < 1024) {
                return size + ' bytes'
            } else if (size >= 1024 && size < 1048576) {
                return (size / 1024).toFixed(1) + ' KB'
            } else if (size >= 1048576) {
                return (size / 1048576).toFixed(1) + ' MB'
            }
        },

        fileValid (file) {
            // Valid accept values
            // A case-insensitive file extension starting with the STOP character (U+002E). (e.g. .jpg, .png, .doc).
            // A valid MIME type with no extensions.
            // audio/* representing sound files. HTML5
            // video/* representing video files. HTML5
            // image/* representing image files. HTML5

            if (this.accept) {
                const fileType = file.type.split('/')[1]
                const fileTypes = this.accept.split(',')

                return fileTypes.includes(fileType)
            }
            return true
        },

        fileExtension (name) {
            const ext = name.match(/(\.[^.]*$)/)
            return ext.length ? ext[0].replace('.', '') : ''
        },

        isPreviewable (file) {
            // TODO: Switch to .match('image/*')
            switch (file.type) {
            case 'image/png':
            case 'image/bmp':
            case 'image/jpg':
            case 'image/jpeg':
            case 'image/gif':
                return true && this.support.fileReader
            default:
                return false
            }
        },

        createPreview (file) {
            // Create the file reader
            const preview = new Image()
            const reader = new FileReader()
            const promise = new Promise((resolve, reject) => {
                // Closure to capture the file information
                reader.onload = event => {
                    const src = event.target.result
                    preview.onload = event => {
                        resolve({
                            src,
                            width: preview.width,
                            height: preview.height
                        })
                    }
                    // set image src
                    preview.src = src
                }
            })
            // Read in the image file as a data URL.
            reader.readAsDataURL(file)
            //  return the promise
            return promise
        },

        convertFileList (fileList) {
            const filesListArray = Array.from(fileList)
            return filesListArray.map(file => {
                return this.convertFile(file)
            })
        },

        convertFile (file) {
            const extendedFile = {
                name: file.name,
                type: file.type,
                lastModified: new Date(file.lastModified),
                size: this.fileSize(file.size),
                sizeValid: this.compareFileSize(file.size),
                ext: this.fileExtension(file.name),
                extValid: this.fileValid(file),
                image: null,
                file
            }

            if (this.isPreviewable(file)) {
                this.createPreview(file).then(image => {
                    // Assign the preview
                    extendedFile.image = image
                })
            }

            return extendedFile
        },

        compareFileSize (size) {
            return size >= this.requirements.minSize && size <= this.requirements.maxSize
        }
    }
}
</script>

<style lang="scss">
    .input-file {
        position: relative;
        padding: 30px;

        &.is-invalid {
            border: 2px solid #d11212;
            color: #d11212;
        }
    }

    .input-file__placeholder {
        min-height: 100px;
        text-align: center;
    }

    .input-file__input {
        position: absolute;
        opacity: 0;
    }

    .input-file__list-item {
        display: flex;
        justify-content: space-between;
        line-height: 60px;
    }

    .input-file__list-type {
        width: 60px;
        height: 60px;

        background-size: cover;
        background-position: center center;

        border-radius: 50%;
        text-transform: uppercase;
        font-weight: bold;
        text-align: center;

    }
</style>



// WEBPACK FOOTER //
// src/elements/InputFile.vue